/**
 * Wave H — dimensioning & annotation commands. The existing CAD dimension
 * renderer family is reused; new subtypes carry data for LinearDimension/
 * Radial renderers. (command-parity-plan.md)
 */

import {
    runInteraction, PT, PK, PN, PLoopK, PTxt, requireSelection, entityToPolyline, makeEntity, rubberBand,
} from './lib';
import { undoableAdd, undoableModify } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useDimStyleStore } from '../../../tools/cad/DimensioningTools';
import { useLeaderStyleStore } from '../../../state/commandAuxStores';
import { isAnnotativeType, applyObjectScale, useAnnotationScaleStore } from '../../../state/annotationScaleStore';
import { updateFields } from '../fields';
import { polylineLength, polygonArea, centroid, lineIntersection2 } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { UX_COLORS } from '../../../themes/colors';
import { notify } from '../../../utils/notify';

function dimEntity(type: string, data: Record<string, unknown>, layerId?: string) {
    return makeEntity('dimension', `Dim-${type}-${Date.now()}`, {
        type,
        // Stamp the CURRENT dim style so the renderers draw this dimension
        // with its own style (styleId), not the active-style snapshot.
        styleId: useDimStyleStore.getState().activeStyleId,
        ...data,
    }, layerId);
}

function textEntity(content: string, pos: Pt3, opts: { multiline?: boolean; styleId?: string } = {}) {
    return makeEntity(opts.multiline ? 'mtext' : 'text', `Text-${Date.now()}`, {
        content, position: pos, styleId: opts.styleId ?? 'standard',
        rotation: 0, multiline: !!opts.multiline,
    });
}

/** DDEDIT helpers (docs/plans/2d plan 5.4): double-click pre-selects a
 *  text entity; the command then jumps straight to the content prompt. */
function selectedEditableText() {
    const { entities, selectedIds } = useAppStore.getState();
    const id = selectedIds.find((i) => ['text', 'mtext', 'dimension', 'attdef'].includes(entities[i]?.type ?? ''));
    return id ? entities[id] : null;
}
function previewOf(e: { data: Record<string, unknown>; type: string }): string {
    const raw = String(e.data.content ?? e.data.override ?? e.data.tag ?? e.type);
    return raw.length > 18 ? `${raw.slice(0, 18)}…` : raw;
}
function applyDdEdit(id: string, content: string, type?: string): string {
    const e = useAppStore.getState().entities[id];
    const etype = type ?? e?.type ?? 'text';
    if (!e) return 'DDEDIT: object not found.';
    undoableModify([id], (d) => {
        const next = { ...d };
        if (etype === 'dimension') next.override = content;
        else if (etype === 'attdef') next.defaultValue = content;
        else next.content = content;
        return next;
    });
    return `DDEDIT: text updated (${etype}).`;
}

function valueNum(v: number | string | undefined): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}

// ---------------- QDIM (Faz 8.1, OCS qdim.rs parity) ----------------

/** Snap priority for candidate generation: curve characteristic points
 *  (endpoints/vertices) or curve-pair intersections. SEttings keyword. */
type QdimSnapPriority = 'endpoints' | 'intersections';
let qdimSnapPriority: QdimSnapPriority = 'endpoints';
/** Excluded candidates (Edit keyword): nearest-candidate picks accumulate
 *  here for the session; the next QDIM run filters them out. */
const qdimExcluded: Pt3[] = [];
/** World-space dedup tolerance (zoom-independent, OCS candidate_tolerance). */
const QDIM_DEDUP_TOL = 0.05;
/** Stagger/baseline offset step (DIMSPACE value). */
const QDIM_DIM_SPACING = 1;

/** Characteristic points of one entity: pline vertices, line ends, arc
 *  endpoints + center, rectangle corners (OCS characteristic_points). */
function qdimCharacteristicPoints(e: Entity): Pt3[] {
    const d = e.data as Record<string, any>;
    if (e.type === 'circle') return [[...d.center] as Pt3];
    if (e.type === 'arc') return [[...d.center] as Pt3];
    const poly = entityToPolyline(e);
    if (!poly || poly.length < 2) return [];
    const pts = [...poly];
    // Drop the duplicated closing vertex of closed loops.
    const first = pts[0], last = pts[pts.length - 1];
    if (pts.length > 2 && Math.hypot(last[0] - first[0], last[1] - first[1]) < 1e-9) pts.pop();
    return pts;
}

/** Dedup a candidate list within the world tolerance. */
function dedupQdimCandidates(pts: Pt3[]): Pt3[] {
    const out: Pt3[] = [];
    for (const p of pts) {
        if (!out.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < QDIM_DEDUP_TOL)) out.push(p);
    }
    return out;
}

/**
 * Candidate dimension points (OCS qdim.rs candidate production): selected
 * entities contribute characteristic points (Endpoints priority) or
 * curve-pair segment intersections (Intersections priority); selected
 * dimensions contribute their reference points. Excluded picks are filtered.
 */
function collectQdimCandidates(entities: Entity[]): Pt3[] {
    const polys = entities
        .filter((e) => e.type !== 'dimension')
        .map((e) => entityToPolyline(e))
        .filter((p): p is Pt3[] => !!p && p.length >= 2);
    const raw: Pt3[] = [];
    if (qdimSnapPriority === 'endpoints') {
        entities.forEach((e) => raw.push(...qdimCharacteristicPoints(e)));
    } else {
        // Intersections: every segment of one entity against every segment
        // of another (OCS curve-pair intersections priority).
        for (let i = 0; i < polys.length; i++) {
            for (let j = i + 1; j < polys.length; j++) {
                for (let a = 0; a < polys[i].length - 1; a++) {
                    for (let b = 0; b < polys[j].length - 1; b++) {
                        const hit = lineIntersection2(
                            { a: polys[i][a], b: polys[i][a + 1] },
                            { a: polys[j][b], b: polys[j][b + 1] },
                        );
                        if (hit) raw.push(hit);
                    }
                }
            }
        }
    }
    // Selected dimensions lend their reference points (OCS selected-
    // dimension candidate source).
    entities.filter((e) => e.type === 'dimension').forEach((e) => {
        (['point1', 'point2'] as const).forEach((k) => {
            const p = e.data[k];
            if (Array.isArray(p) && p.length >= 2) raw.push([...p] as Pt3);
        });
    });
    const deduped = dedupQdimCandidates(raw);
    // Filter session-excluded candidates (Edit keyword picks).
    return deduped.filter((p) => !qdimExcluded.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < QDIM_DEDUP_TOL));
}

/**
 * Orientation from the candidate bbox + placement click (OCS orientation):
 * the dimension measures along the dominant bbox axis; the offset side
 * follows which half of the bbox the click landed in.
 */
function qdimOrientation(candidates: Pt3[], place: Pt3): { horizontal: boolean; side: 1 | -1 } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    candidates.forEach((p) => {
        minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
        minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
    });
    const midY = (minY + maxY) / 2;
    const horizontal = (maxX - minX) >= (maxY - minY);
    const side: 1 | -1 = place[1] >= midY ? 1 : -1;
    return { horizontal, side };
}

export const dimRunners: RunnerMap = {    // Active tool for DIMLINEAR-style placement, exposed as actions so the
    // command line can also start them via preset/registry.
    'draw.mtext': () => {
        runInteraction({
            steps: [PTxt('MTEXT: text content'), PT('MTEXT: insertion point')],
            complete: (pts, values) => {
                undoableAdd([textEntity(String(values[0] ?? 'Text'), [pts[0].x, pts[0].y, pts[0].z], { multiline: true })]);
                return 'Multiline text placed.';
            },
        });
        return 'MTEXT: enter text';
    },
    'draw.text': () => {
        // Actually activate the CAD text hook (ToolScope mounts the 'text'
        // tool scope); previously this returned a message and did nothing.
        useAppStore.getState().setActiveTool('text');
        return 'TEXT tool activated via command.';
    },

    // DDEDIT: edit placed text/mtext/dimension content. With PICKFIRST and a
    // text already selected, the command goes straight to the new-content
    // prompt (double-click on the text pre-selects it — see EntityLayer).
    'draw.ddedit': () => {
        const selected = selectedEditableText();
        if (selected) {
            runInteraction({
                steps: [PTxt(`DDEDIT: new content for "${previewOf(selected)}"`)],
                complete: (_p, values) => applyDdEdit(selected.id, String(values[0] ?? '')),
            });
            return 'DDEDIT: type the new text content';
        }
        runInteraction({
            steps: [
                PK('DDEDIT: pick text or a dimension to edit', ['text', 'mtext', 'dimension', 'attdef']),
                PTxt('DDEDIT: new text content'),
            ],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'DDEDIT: invalid pick.';
                return applyDdEdit(e.id, String(values[1] ?? ''), e.type);
            },
        });
        return 'DDEDIT: pick the text to edit';
    },

    'dim.diameter': () => {
        runInteraction({
            steps: [PK('DIMDIAMETER: pick a circle/arc')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'DIMDIAMETER: invalid pick.';
                const c = (e.data.center as number[]) ?? [0, 0, 0];
                const r = (e.data.radius as number) ?? 1;
                undoableAdd([dimEntity('diameter', { center: c, radiusPoint: [c[0] + r, c[1], c[2] ?? 0], radius: r, textPosition: [c[0], c[1] + r * 0.4, (c[2] ?? 0)] })]);
                return `Diameter dimension Ø${(r * 2).toFixed(2)} added.`;
            },
        });
        return 'DIMDIAMETER: pick a circle';
    },

    'dim.angular': () => {
        runInteraction({
            steps: [PK('DIMANGULAR: pick first line'), PK('DIMANGULAR: pick second line'), PT('DIMANGULAR: text location')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const l1 = entityToPolyline(entities[picks[0]]);
                const l2 = entityToPolyline(entities[picks[1]]);
                if (!l1 || !l2) return 'DIMANGULAR: pick two lines.';
                // Only one point step exists: the text location is pts[0].
                const textP = [pts[0].x, pts[0].y, pts[0].z];
                const d1 = [l1[l1.length - 1][0] - l1[0][0], l1[l1.length - 1][1] - l1[0][1]];
                const d2 = [l2[l2.length - 1][0] - l2[0][0], l2[l2.length - 1][1] - l2[0][1]];
                const cos = (d1[0] * d2[0] + d1[1] * d2[1])
                    / (Math.hypot(d1[0], d1[1]) * Math.hypot(d2[0], d2[1]) || 1);
                const deg = Math.abs(Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI);
                // Vertex = where the two measured lines meet (infinite-line
                // solution); the arc renderer draws rays + measurement arc
                // from it, so the label shows the ANGLE not a chord.
                const vertex = lineIntersection2(
                    { a: l1[0], b: l1[l1.length - 1] },
                    { a: l2[0], b: l2[l2.length - 1] }
                ) ?? [(l1[0][0] + l2[0][0]) / 2, (l1[0][1] + l2[0][1]) / 2, l1[0][2] ?? 0];
                undoableAdd([dimEntity('angular', {
                    center: vertex,
                    point1: l1[l1.length - 1], point2: l2[l2.length - 1],
                    angle: deg,
                    textPosition: textP,
                    override: `${deg.toFixed(1)}°`,
                })]);
                return `Angular dimension ${deg.toFixed(1)}° added.`;
            },
        });
        return 'DIMANGULAR: pick first line';
    },

    'dim.arcLength': () => {
        runInteraction({
            steps: [PK('DIMARC: pick an arc'), PT('DIMARC: text location')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const poly = entityToPolyline(entities[picks[0]]);
                if (!poly) return 'DIMARC: unsupported object.';
                const len = polylineLength(poly);
                undoableAdd([dimEntity('arcLength', {
                    point1: poly[0], point2: poly[poly.length - 1],
                    // The own-subtype renderer follows the ARC path (not the
                    // chord): pass the tessellation + the computed length.
                    arcPts: poly.map((q) => [...q]),
                    arcLength: len,
                    textPosition: [pts[0].x, pts[0].y, pts[0].z],
                    override: len.toFixed(2),
                })]);
                return `Arc-length dimension (${len.toFixed(3)}) added.`;
            },
        });
        return 'DIMARC: pick an arc';
    },

    'dim.continue': () => {
        runInteraction({
            steps: [PK('DIMCONTINUE: pick an existing dimension'), PT('DIMCONTINUE: next extension point')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = entities[picks[0]];
                if (!src || src.type !== 'dimension') return 'DIMCONTINUE: pick a dimension first.';
                const prev = (src.data.point2 as Pt3) ?? [pts[0].x, pts[0].y, pts[0].z];
                undoableAdd([dimEntity('continue', { point1: prev, point2: [pts[0].x, pts[0].y, pts[0].z], textPosition: [pts[0].x, pts[0].y + 0.5, pts[0].z] })]);
                return 'Chained dimension continued.';
            },
        });
        return 'DIMCONTINUE: pick a dimension';
    },

    'dim.baseline': () => {
        runInteraction({
            steps: [PK('DIMBASELINE: pick the base dimension'), PT('DIMBASELINE: next point')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = entities[picks[0]];
                if (!src || src.type !== 'dimension') return 'DIMBASELINE: pick a dimension first.';
                const origin = (src.data.point1 as Pt3) ?? [pts[0].x, pts[0].y, pts[0].z];
                undoableAdd([dimEntity('baseline', { point1: origin, point2: [pts[0].x, pts[0].y, pts[0].z], textPosition: [pts[0].x, pts[0].y + 1, pts[0].z] })]);
                return 'Baseline dimension added.';
            },
        });
        return 'DIMBASELINE: pick a dimension';
    },

    'dim.jogged': () => {
        runInteraction({
            steps: [PK('DIMJOGGED: pick an arc'), PT('DIMJOGGED: text location')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const c = entities[picks[0]]?.data.center as number[] | undefined;
                if (!c) return 'DIMJOGGED: pick a circle/arc.';
                undoableAdd([dimEntity('jogged', { center: c, radius: entities[picks[0]].data.radius ?? 1, radiusPoint: [pts[0].x, pts[0].y, pts[0].z] })]);
                return 'Jogged radius dimension added.';
            },
        });
        return 'DIMJOGGED: pick an arc';
    },

    'dim.space': () => {
        runInteraction({
            steps: [PLoopK('DIMSPACE: pick parallel linear dimensions (2+)', 2), PN('DIMSPACE: spacing <1>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const dims = picks.map((id) => entities[id]).filter((e) => e?.type === 'dimension');
                if (dims.length < 2) return 'DIMSPACE: pick at least two dimensions.';
                const spacing = Number(values[1]) > 0 ? Number(values[1]) : 1;
                // Sort by distance of textPosition from the first dim's base
                // line and re-stack them with uniform spacing along the
                // average extension direction.
                const first = dims[0];
                const p1 = (first.data.point1 as number[]) ?? [0, 0, 0];
                const p2 = (first.data.point2 as number[]) ?? [1, 0, 0];
                let ux = p2[1] - p1[1], uy = -(p2[0] - p1[0]);
                const ul = Math.hypot(ux, uy) || 1;
                ux /= ul; uy /= ul;
                const proj = (e: typeof first) => {
                    const t = (e.data.textPosition as number[]) ?? [0, 0, 0];
                    return (t[0] - p1[0]) * ux + (t[1] - p1[1]) * uy;
                };
                const sorted = [...dims].sort((a, b) => proj(a) - proj(b));
                const base = proj(sorted[0]);
                const ids = sorted.map((d) => d.id);
                const snapshots = new Map(ids.map((id) => [id, JSON.stringify(entities[id].data)]));
                undoableModify(ids, (d, e) => {
                    const idx = ids.indexOf(e.id);
                    const tp = Array.isArray(d.textPosition) ? [...(d.textPosition as number[])] : [p1[0], p1[1], p1[2] ?? 0];
                    const cur = proj(e);
                    tp[0] += (base + idx * spacing - cur) * ux;
                    tp[1] += (base + idx * spacing - cur) * uy;
                    return { ...d, textPosition: tp };
                });
                void snapshots;
                return `DIMSPACE: ${ids.length} dimensions spaced at ${spacing}.`;
            },
        });
        return 'DIMSPACE: pick parallel dimensions';
    },

    'dim.break': () => {
        runInteraction({
            steps: [PK('DIMBREAK: pick a dimension')],
            complete: (_p, _v, picks) => {
                undoableModify(picks, (d) => ({ ...d, breakAtCrossing: true }));
                return 'DIMBREAK: crossing break flag set.';
            },
        });
        return 'DIMBREAK: pick a dimension';
    },

    // QDIM (Faz 8.1, OCS qdim.rs parity): batch dimensioning over a
    // selection. Candidate points come from curve characteristic points or
    // curve-pair intersections (SEttings), the mode keyword picks the
    // dimension family, and the placement click resolves orientation + side.
    'dim.qdim': () => {
        // Fresh session: a new QDIM run regenerates every candidate (Edit
        // exclusions persist across runs within the session).
        qdimExcluded.length = 0;
        runInteraction({
            steps: [
                PLoopK('QDIM: select geometry to dimension', 1),
                {
                    prompt: 'QDIM: mode (Continuous/Staggered/Baseline/Ordinate/Radius/Diameter/Edit/SEttings) <Continuous>',
                    kind: 'text' as const,
                    options: [
                        { keyword: 'Continuous' }, { keyword: 'Staggered' }, { keyword: 'Baseline' },
                        { keyword: 'Ordinate' }, { keyword: 'Radius' }, { keyword: 'Diameter' },
                        { keyword: 'Edit' }, { keyword: 'SEttings' },
                    ],
                },
                PT('QDIM: placement point'),
            ],
            complete: (pts, values, picks) => {
                const { entities } = useAppStore.getState();
                const selected = picks.map((id) => entities[id]).filter(Boolean) as Entity[];
                if (selected.length === 0) return 'QDIM: nothing selected.';
                const place: Pt3 = [pts[0].x, pts[0].y, pts[0].z];
                const modeRaw = String(values[1] ?? 'C').trim().toLowerCase();
                const candidates = collectQdimCandidates(selected);
                const candidatesOut = (label: string, n: number): string =>
                    n === 0
                        ? `QDIM ${label}: no candidate points found (selection: curves/lines/polylines).`
                        : `QDIM ${label}: ${n} dimension(s) placed from ${candidates.length} candidate point(s).`;

                // SEttings: the placement pick is ignored; switch the priority.
                if (modeRaw === 'se' || modeRaw === 'settings') {
                    qdimSnapPriority = qdimSnapPriority === 'endpoints' ? 'intersections' : 'endpoints';
                    return `QDIM SEttings: snap priority = ${qdimSnapPriority === 'endpoints' ? 'E (characteristic points)' : 'I (intersections)'}.`;
                }
                // Edit: the placement pick excludes the nearest candidate.
                if (modeRaw === 'e' || modeRaw === 'edit') {
                    let best = -1, bestDist = Infinity;
                    candidates.forEach((c, i) => {
                        const d = Math.hypot(c[0] - place[0], c[1] - place[1]);
                        if (d < bestDist) { bestDist = d; best = i; }
                    });
                    if (best < 0 || bestDist > QDIM_DIM_SPACING) return 'QDIM Edit: no candidate near the pick.';
                    qdimExcluded.push(candidates[best]);
                    return `QDIM Edit: candidate excluded (${qdimExcluded.length} total) — re-run QDIM to regenerate without it.`;
                }

                if (candidates.length < 2 && (modeRaw === 'c' || modeRaw === 's' || modeRaw === 'b' || modeRaw === 'o')) {
                    return 'QDIM: fewer than two candidate points — nothing to dimension.';
                }
                const { horizontal, side } = qdimOrientation(candidates, place);
                // Sort along the measured axis (x for horizontal, y vertical).
                const axis = horizontal ? 0 : 1;
                const sorted = [...candidates].sort((a, b) => a[axis] - b[axis]);
                const off = (p: Pt3, dist: number): [number, number, number] => horizontal
                    ? [p[0], p[1] + side * dist, p[2]]
                    : [p[0] + side * dist, p[1], p[2]];
                const created: Entity[] = [];
                const styleId = useDimStyleStore.getState().activeStyleId;

                if (modeRaw === 'r' || modeRaw === 'radius' || modeRaw === 'di' || modeRaw === 'diameter') {
                    // Radius/Diameter: circles + arcs from the selection.
                    const wantDiameter = modeRaw === 'di' || modeRaw === 'diameter';
                    const curves = selected.filter((e) => e.type === 'circle' || e.type === 'arc');
                    if (curves.length === 0) return 'QDIM: pick circles/arcs for Radius/Diameter mode.';
                    curves.forEach((e) => {
                        const c = (e.data.center as number[]) ?? [0, 0, 0];
                        const r = (e.data.radius as number) ?? 1;
                        // Rim point toward the placement click (OCS chord dir).
                        const ang = Math.atan2(place[1] - c[1], place[0] - c[0]);
                        const rim: [number, number, number] = [c[0] + Math.cos(ang) * r, c[1] + Math.sin(ang) * r, c[2] ?? 0];
                        created.push(dimEntity(wantDiameter ? 'diameter' : 'radius', {
                            center: c, radius: r, radiusPoint: rim, styleId,
                            textPosition: off(rim, 0.5),
                        }));
                    });
                    undoableAdd(created);
                    return candidatesOut(wantDiameter ? 'Diameter' : 'Radius', created.length);
                }
                if (modeRaw === 'o' || modeRaw === 'ordinate') {
                    sorted.forEach((p) => {
                        created.push(dimEntity('ordinate', {
                            point1: [...p] as [number, number, number],
                            point2: off(p, 0.35),
                            axis: horizontal ? 'x' : 'y',
                            styleId,
                            override: (p[axis]).toFixed(2),
                        }));
                    });
                    undoableAdd(created);
                    return candidatesOut('Ordinate', created.length);
                }

                // Linear families: consecutive pairs (Continuous/Staggered) or
                // datum pairs (Baseline), offsets along the free axis.
                if (modeRaw === 'b' || modeRaw === 'baseline') {
                    const datum = sorted[0];
                    sorted.slice(1).forEach((p, i) => {
                        created.push(dimEntity('baseline', {
                            point1: [...datum] as [number, number, number],
                            point2: [...p] as [number, number, number],
                            textPosition: off(p, 0.5 + (i + 1) * QDIM_DIM_SPACING * 0.6),
                            styleId,
                        }));
                    });
                } else {
                    const staggered = modeRaw === 's' || modeRaw === 'staggered';
                    for (let i = 0; i < sorted.length - 1; i++) {
                        const p1 = sorted[i], p2 = sorted[i + 1];
                        created.push(dimEntity('linear', {
                            point1: [...p1] as [number, number, number],
                            point2: [...p2] as [number, number, number],
                            textPosition: off(p2, staggered ? 0.5 + i * QDIM_DIM_SPACING : 0.5),
                            styleId,
                            // Staggered slots ride the renderer's DIMSPACE offset.
                            ...(staggered ? { spaceIndex: i, spaceOffset: QDIM_DIM_SPACING } : {}),
                        }));
                    }
                }
                undoableAdd(created);
                return candidatesOut(
                    modeRaw === 'b' || modeRaw === 'baseline' ? 'Baseline'
                        : (modeRaw === 's' || modeRaw === 'staggered') ? 'Staggered' : 'Continuous',
                    created.length,
                );
            },
        });
        return 'QDIM: select geometry, then a mode keyword (C/S/B/O/R/DI/E/SE)';
    },

    'dim.style': (args) => {
        // DIMSTYLE with a name argument creates/switches directly; with no
        // arguments open the full Dimension Style Manager dialog (parity
        // with the 2D DIMSTYLE command).
        const name = args?.join(' ')?.trim();
        const store = useDimStyleStore.getState();
        if (!name) {
            useAppStore.getState().openManageDialog('dimStyles');
            return 'DIMSTYLE: Dimension Style Manager opened.';
        }
        const existing = Object.values(store.styles).find((s) => s.name.toLowerCase() === name.toLowerCase());
        if (existing) {
            store.setActiveStyle(existing.id);
            return `DIMSTYLE: style "${existing.name}" set current.`;
        }
        const base = store.getActiveStyle();
        const id = name.toLowerCase().replace(/\s+/g, '-');
        store.addStyle({ ...base, id, name });
        store.setActiveStyle(id);
        return `Dimension style "${name}" created and activated.`;
    },

    'dim.reassociate': () => {
        runInteraction({
            steps: [PK('DIMREASSOCIATE: pick a dimension'), PK('DIMREASSOCIATE: pick the geometry to bind to')],
            complete: (_p, _v, picks) => {
                const src = useAppStore.getState().entities[picks[1]];
                const ref = src ? entityToPolyline(src) : null;
                undoableModify([picks[0]], (d) => ({
                    ...d,
                    assoc: [picks[1]],
                    // Bind-time snapshot of the source geometry: the associator
                    // engine (core/commands/dimAssociation) replays source
                    // movements onto this dimension's definition points.
                    assocRef: ref ? { pts: ref.map((q) => [...q]), centroid: centroid(ref) } : undefined,
                }));
                return 'Dimension reassociated to the picked geometry (associative).';
            },
        });
        return 'DIMREASSOCIATE: pick dimension then geometry';
    },
    'dim.disassociate': () => {
        const list = requireSelection('DIMDISASSOCIATE');
        if (!list) return 'DIMDISASSOCIATE: select dimensions first.';
        undoableModify(list.map((e) => e.id), (d) => { delete d.assoc; return d; });
        return `DIMDISASSOCIATE: ${list.length} object(s) detached.`;
    },
    'dim.inspect': () => {
        runInteraction({
            steps: [PK('DIMINSPECT: pick a dimension'), PTxt('DIMINSPECT: inspection label')],
            complete: (_p, values, picks) => {
                undoableModify(picks, (d) => ({ ...d, inspection: String(values[1] ?? 'OK') }));
                return 'Inspection label attached to the dimension.';
            },
        });
        return 'DIMINSPECT: pick dimension';
    },
    'dim.override': () => {
        runInteraction({
            steps: [PK('DIMOVERRIDE: pick a dimension'), PTxt('DIMOVERRIDE: override text')],
            complete: (_p, values, picks) => {
                undoableModify(picks, (d) => ({ ...d, override: String(values[1] ?? '') }));
                return 'Dimension text overridden.';
            },
        });
        return 'DIMOVERRIDE: pick dimension';
    },
    'dim.centerMark': () => {
        runInteraction({
            steps: [PK('CENTERMARK: pick a circle/arc'), PN('CENTERMARK: mark size <0.3>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const c = entities[picks[0]]?.data.center as number[] | undefined;
                if (!c) return 'CENTERMARK: pick a circle/arc.';
                const s = Number(values[1]) || 0.3;
                undoableAdd([
                    makeEntity('line', `CenterMark-${Date.now()}`, { start: [c[0] - s, c[1], c[2] ?? 0], end: [c[0] + s, c[1], c[2] ?? 0], color: '#cc4444' }),
                    makeEntity('line', `CenterMark-${Date.now()}`, { start: [c[0], c[1] - s, c[2] ?? 0], end: [c[0], c[1] + s, c[2] ?? 0], color: '#cc4444' }),
                ]);
                return 'Center mark added.';
            },
        });
        return 'CENTERMARK: pick a circle';
    },
    'dim.tolerance': () => {
        // Faz 8.3: the structural GD&T dialog (1 symbol + 2 tolerance + 3
        // datum compartments) places a typed 'tolerance' entity; the legacy
        // prompt flow stays reachable through the dialog's structural model.
        useAppStore.getState().openManageDialog('tolerance');
        return 'TOLERANCE: Geometric Tolerance dialog opened.';
    },

    // ---------------- annotation ----------------
    'ann.leader': () => {
        const ls = useLeaderStyleStore.getState();
        const style = ls.styles[ls.activeId] ?? ls.styles['standard'];
        runInteraction({
            steps: [PT('MLEADER: arrow tip point'), PT('MLEADER: landing point'), PTxt('MLEADER: note text')],
            complete: (pts, values) => {
                undoableAdd([makeEntity('leader', `Leader-${Date.now()}`, {
                    tip: [pts[0].x, pts[0].y, pts[0].z], landing: [pts[1].x, pts[1].y, pts[1].z],
                    text: String(values[2] ?? 'Note'),
                    styleId: style.id,
                    color: style.color,
                    textScale: style.textScale,
                })]);
                return `Multileader placed (style "${style.name}").`;
            },
        });
        return 'MLEADER: tip point';
    },
    // QLEADER: same leader entity, simpler 3-pick flow (tip, knee, text).
    'ann.qleader': () => {
        runInteraction({
            steps: [PT('QLEADER: first leader point'), PT('QLEADER: next point'), PTxt('QLEADER: annotation text')],
            preview: rubberBand(UX_COLORS.ghost),
            complete: (pts, values) => {
                const tip = pts[0], knee = pts[1];
                undoableAdd([makeEntity('leader', `Leader-${Date.now()}`, {
                    tip: [tip.x, tip.y, tip.z],
                    landing: [knee.x, knee.y, knee.z],
                    text: String(values[2] ?? 'Note'),
                    qleader: true,
                })]);
                return 'Quick leader placed.';
            },
        });
        return 'QLEADER: specify first leader point';
    },
    'ann.leaderStyle': () => {
        runInteraction({
            steps: [
                PTxt('MLEADERSTYLE: style name'),
                PN('MLEADERSTYLE: arrow scale <1>'),
                PN('MLEADERSTYLE: text scale <1>'),
                PTxt('MLEADERSTYLE: color (hex or "keep")'),
            ],
            complete: (_p, values) => {
                const name = String(values[0] ?? 'Standard').trim();
                const id = name.toLowerCase().replace(/\s+/g, '-');
                const st = useLeaderStyleStore.getState();
                const base = st.styles[id] ?? st.styles[st.activeId];
                const color = String(values[3] ?? 'keep').trim();
                st.save({
                    id, name,
                    arrowScale: valueNum(values[1]) || 1,
                    textScale: valueNum(values[2]) || 1,
                    color: color && color !== 'keep' ? color : base.color,
                });
                return `Leader style "${name}" saved and set current.`;
            },
        });
        return 'MLEADERSTYLE: enter name';
    },
    'ann.leaderCollect': () => {
        const list = requireSelection('MLEADERCOLLECT');
        if (!list) return 'MLEADERCOLLECT: select leaders first.';
        const leaders = list.filter((e) => e.type === 'leader');
        if (leaders.length === 0) return 'MLEADERCOLLECT: no leaders selected.';
        // Bubble stack: order leaders by tip height and re-space their
        // landings into an evenly spaced column (visible collection).
        const sorted = [...leaders].sort((a, b) =>
            ((b.data.tip as number[])?.[1] ?? 0) - ((a.data.tip as number[])?.[1] ?? 0));
        const stackX = Math.max(...sorted.map((l) => ((l.data.landing as number[])?.[0] ?? 0)));
        const baseY = (sorted[0].data.landing as number[])?.[1] ?? 0;
        const ids = sorted.map((l) => l.id);
        undoableModify(ids, (d, e) => {
            const idx = ids.indexOf(e.id);
            const tip = (d.tip as number[]) ?? [0, 0, 0];
            const y = baseY - idx * 0.5;
            return {
                ...d,
                landing: [stackX, y, tip[2]],
                textPosition: [stackX + 0.15, y, tip[2]],
                collected: `${idx}`,
            };
        });
        return `MLEADERCOLLECT: ${ids.length} leaders stacked into a bubble column.`;
    },
    'ann.leaderAlign': () => {
        const list = requireSelection('MLEADERALIGN');
        if (!list) return 'MLEADERALIGN: select leaders first.';
        const leaders = list.filter((e) => e.type === 'leader');
        if (leaders.length < 2) return 'MLEADERALIGN: pick 2+ leaders.';
        const ys = leaders.map((e) => ((e.data.landing as number[]) ?? [0, 0, 0])[1]);
        const avg = ys.reduce((a, b) => a + b, 0) / ys.length;
        leaders.forEach((e) => {
            const land = (e.data.landing as number[]) ?? [0, 0, 0];
            undoableModify([e.id], (d) => ({ ...d, landing: [land[0], avg, land[2] ?? 0] }));
        });
        return `${leaders.length} leaders aligned.`;
    },
    // FIELD (Faz 8.8): the dialog builds %<…>% codes (Date & Time, Document,
    // Object property, Formula, System Variable) and places a live mtext.
    'ann.field': () => {
        useAppStore.getState().openManageDialog('field');
        return 'FIELD: Field dialog opened.';
    },
    'ann.updateField': () => {
        // UPDATEFIELD (FIELDEVAL bit 32 — explicit, mask-independent):
        // re-resolve every field host (or just the selection) + the legacy
        // fieldRef area bindings.
        const list = requireSelection('UPDATEFIELD');
        const hosts = Object.values(useAppStore.getState().entities)
            .filter((e) => (e.type === 'text' || e.type === 'mtext') && /%<.*?%>/.test(String(e.data.content ?? '')));
        const scoped = list && list.length > 0 ? hosts.filter((h) => list.some((s) => s.id === h.id)) : hosts;
        const n = scoped.length > 0 ? updateFields('explicit') : 0;
        let legacy = 0;
        const legacyTargets = list && list.length > 0 ? list : Object.values(useAppStore.getState().entities);
        legacyTargets.forEach((e) => {
            const refId = e.data.fieldRef as string | undefined;
            if (!refId) return;
            const src = useAppStore.getState().entities[refId];
            if (!src) return;
            const poly = entityToPolyline(src);
            const area = poly && poly.length >= 3 ? polygonArea(poly).toFixed(2) : '—';
            undoableModify([e.id], (d) => ({ ...d, content: `Area = ${area}` }));
            legacy++;
        });
        const parts: string[] = [];
        if (n > 0) parts.push(`${n} field host(s)`);
        if (legacy > 0) parts.push(`${legacy} legacy binding(s)`);
        return parts.length > 0 ? `UPDATEFIELD: ${parts.join(', ')} refreshed.` : 'UPDATEFIELD: no field texts found.';
    },

    // FIND (Faz 8.2): opens the FIND/REPLACE dialog (scanner core lives in
    // core/commands/findReplace.ts).
    'find.replace': () => {
        useAppStore.getState().openManageDialog('findReplace');
        return 'FIND: Find and Replace dialog opened.';
    },

    // OBJECTSCALE / ANNORESET / SCALELISTEDIT (Faz 8.4, OCS annotative
    // maintenance API parity).
    'ann.objectscale': () => {
        const list = requireSelection('OBJECTSCALE');
        if (!list) return 'OBJECTSCALE: select objects first.';
        const eligible = list.filter((e) => isAnnotativeType(e.type));
        if (eligible.length === 0) return 'OBJECTSCALE: the selection has no annotative-capable objects (text/mtext/dimension/leader/tolerance/hatch/attdef/insert).';
        useAppStore.getState().openManageDialog('objectScale');
        return `OBJECTSCALE: editing annotative membership of ${eligible.length} object(s).`;
    },
    'ann.annoreset': () => {
        const list = requireSelection('ANNORESET');
        if (!list) return 'ANNORESET: select objects first.';
        const n = applyObjectScale(list.map((e) => e.id), 'reset');
        return n > 0
            ? `ANNORESET: ${n} object(s) reset to their native scale.`
            : 'ANNORESET: no annotative objects in the selection.';
    },
    'ann.scalelistedit': () => {
        useAppStore.getState().openManageDialog('scaleListEdit');
        return 'SCALELISTEDIT: annotation scale list editor opened.';
    },
    'ann.scalelisteditDash': (args) => {
        const store = useAnnotationScaleStore.getState();
        const name = args?.join(' ')?.trim();
        if (name && store.scaleList.some((s) => s.name === name)) {
            store.setCurrentScale(name);
            return `-SCALELISTEDIT: current annotation scale set to "${name}".`;
        }
        const list = store.scaleList.map((s) => s.name).join(', ');
        return name
            ? `-SCALELISTEDIT: no scale "${name}" — known scales: ${list}`
            : `-SCALELISTEDIT: current scale "${store.currentScale}" — usage: -SCALELISTEDIT <name>`;
    },
};
